import { useEffect, useState, useCallback } from "react";
import { Bell, BadgeCheck, Users, DollarSign, Info, Check } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const ICONS = { subscriber: Users, earning: DollarSign, verification: BadgeCheck };

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "ora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  return `${Math.floor(diff / 86400)} g fa`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch (e) {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read");
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && unread) markAll(); }}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="fixed top-4 right-4 md:top-5 md:right-6 z-40 w-11 h-11 rounded-full glass border border-white/10 flex items-center justify-center text-white/80 hover:text-white hover:border-[#E91E8C]/50 transition-colors"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span data-testid="notification-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E91E8C] text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-[#1A1A1A] border-white/10 text-white mr-2" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <span className="font-semibold text-sm">Notifiche</span>
          {items.some((n) => !n.read) && (
            <button onClick={markAll} data-testid="notif-mark-all" className="text-xs text-[#E91E8C] flex items-center gap-1 hover:underline">
              <Check className="w-3 h-3" /> Segna lette
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-white/40 text-sm text-center py-10" data-testid="notif-empty">Nessuna notifica ancora.</p>
          ) : (
            items.map((n) => {
              const Icon = ICONS[n.type] || Info;
              return (
                <div key={n.id} data-testid={`notif-${n.id}`} className={`flex gap-3 px-4 py-3 border-b border-white/5 ${!n.read ? "bg-[#E91E8C]/5" : ""}`}>
                  <div className="w-8 h-8 rounded-full bg-[#E91E8C]/15 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-[#E91E8C]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium leading-tight">{n.title}</p>
                    {n.body && <p className="text-xs text-white/50 mt-0.5">{n.body}</p>}
                    <p className="text-[10px] text-white/30 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
